import React, { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Dialog } from '../../components/ui/Dialog';
import { Input, Label, Separator } from '../../components/ui/Input';
import { useLanguage } from '../../lib/i18n';

interface DeleteAccountCardProps {
  onNotify: (kind: ToastKind, text: string) => void;
  /** Bu cihazdaki oturumu kapatır. */
  onSignOut: () => void;
}

/**
 * Ayarlar > Genel > Hesap: çıkış yap ve hesabı sil. Silmenin geri dönüşü yok:
 * kullanıcı e-posta adresini yazarak onaylıyor (sunucu da aynı adresi kontrol ediyor).
 */
export const DeleteAccountCard: React.FC<DeleteAccountCardProps> = ({ onNotify, onSignOut }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [typed, setTyped] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTyped('');
    void window.zabit
      .getProfile()
      .then((profile) => setEmail(profile.email))
      .catch(() => setEmail(''));
  }, [open]);

  const matches = Boolean(email) && typed.trim().toLocaleLowerCase('tr') === email.toLocaleLowerCase('tr');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!matches) return;
    setIsDeleting(true);
    try {
      await window.zabit.deleteAccount(typed.trim());
      onNotify('success', t('Hesabın silindi.', 'Your account has been deleted.'));
      // Oturum kapandı — uygulamayı baştan açınca giriş ekranı geliyor.
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      onNotify('error', (err as Error).message || t('Hesap silinemedi.', 'Could not delete the account.'));
      setIsDeleting(false);
    }
  };

  return (
    <>
      <section className="space-y-2.5">
        <h2 className="text-sm font-medium text-muted-foreground">{t('Hesap', 'Account')}</h2>
        {/* Çıkış en altta: sayfanın sonunda yanlışlıkla silmeye dokunulmasın. */}
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <p className="font-medium leading-none">{t('Hesabı sil', 'Delete account')}</p>
              <p className="text-[0.8rem] text-muted-foreground">
                {t(
                  'Hesabın, toplantıların, transkriptlerin ve buluttaki kayıtların kalıcı olarak silinir.',
                  'Your account, meetings, transcripts and cloud recordings are permanently deleted.'
                )}
              </p>
            </div>
            <Button variant="destructive" onClick={() => setOpen(true)}>
              {t('Hesabı sil', 'Delete account')}
            </Button>
          </CardContent>
          <Separator />
          <CardContent className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <p className="font-medium leading-none">{t('Çıkış yap', 'Sign out')}</p>
              <p className="text-[0.8rem] text-muted-foreground">
                {t(
                  'Bu cihazdaki oturumun kapanır; toplantıların hesabında kalır.',
                  'You’re signed out on this device; your meetings stay in your account.'
                )}
              </p>
            </div>
            <Button variant="outline" onClick={onSignOut}>
              {t('Çıkış yap', 'Sign out')}
            </Button>
          </CardContent>
        </Card>
      </section>

      <Dialog
        open={open}
        onClose={() => !isDeleting && setOpen(false)}
        title={t('Hesabın kalıcı olarak silinsin mi?', 'Permanently delete your account?')}
        description={t(
          'Bu işlem geri alınamaz. Aktif aboneliğin iptal edilir, toplantıya gönderilmiş botlar durdurulur; toplantıların, transkriptlerin, özetlerin ve buluttaki ses kayıtların silinir. Google Play ya da App Store üzerinden aldığın aboneliği mağazadan ayrıca iptal et; yoksa mağaza yenilemeye devam eder. Bu cihazda kalan ses dosyalarına dokunulmaz.',
          'This can’t be undone. Your active subscription is canceled and any bots sent to meetings are stopped; your meetings, transcripts, summaries and cloud recordings are deleted. A subscription bought through Google Play or the App Store must also be canceled there, or the store keeps renewing it. Audio files left on this device aren’t touched.'
        )}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={isDeleting}>
              {t('Vazgeç', 'Cancel')}
            </Button>
            <Button
              type="submit"
              form="delete-account-form"
              disabled={!matches || isDeleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="size-4 animate-spin" />}
              {t('Hesabımı sil', 'Delete my account')}
            </Button>
          </>
        }
      >
        <form id="delete-account-form" onSubmit={(event) => void submit(event)} className="grid gap-2">
          <Label htmlFor="delete-account-email">
            {email
              ? t(`Onaylamak için ${email} yaz`, `Type ${email} to confirm`)
              : t('Onaylamak için e-posta adresini yaz', 'Type your email address to confirm')}
          </Label>
          <Input
            id="delete-account-email"
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setTyped(event.target.value)}
            disabled={isDeleting}
          />
        </form>
      </Dialog>
    </>
  );
};
